/**
 * Carbon Market Pathways — Dedicated Page
 * Route: /carbon-pathways
 * Shows 4 structured pathways for SMEs based on their profile.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useJourney } from "@/context/JourneyContext";
import client from "@/api/client";
import { GitBranch, CheckCircle2, ArrowLeft, TrendingDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const PATHWAYS = [
  {
    id:          "insetting",
    rank:        1,
    name:        "Insetting (Supply Chain)",
    badge:       "High Eligibility",
    badgeColor:  "bg-emerald-100 text-emerald-700 border-emerald-300",
    description: "Direct emissions reduction within your value chain. You provide verified low-carbon fuel or services to clients — they claim the reduction. No credit sales required.",
    why:         "Your fuel switching and efficiency actions directly reduce emissions for your clients, making insetting the most accessible and immediate pathway.",
    requirements:["Documented client fuel-switching or service agreements", "Baseline emissions established (complete)", "Buyer MOU or supply contract"],
    timeline:    "3–6 months",
    cta:         null,
  },
  {
    id:          "vcm",
    rank:        2,
    name:        "Voluntary Carbon Market (VCM)",
    badge:       "Medium Eligibility",
    badgeColor:  "bg-amber-100 text-amber-700 border-amber-300",
    description: "Pool your verified emissions reductions with other SMEs into a structured carbon project registered under Gold Standard or Verra VCS.",
    why:         "Your identified reductions are suitable for VCM exploration. Aggregation is required to reach a viable project scale for methodology registration.",
    requirements:["Third-party additionality study", "Methodology fit assessment (Gold Standard ACM0002 / Verra AMS-III.B)", "Aggregation with minimum 500t CO₂e/yr portfolio"],
    timeline:    "12–18 months (via aggregation)",
    cta:         "join",
  },
  {
    id:          "poa",
    rank:        3,
    name:        "Programmatic (PoA)",
    badge:       "Low Eligibility (standalone)",
    badgeColor:  "bg-slate-100 text-slate-600 border-slate-300",
    description: "A Programme of Activities bundles multiple SMEs into a single carbon programme — reducing per-participant costs for MRV and certification.",
    why:         "Insufficient standalone scale. A PoA approach distributes the fixed costs of MRV and certification across the programme portfolio.",
    requirements:["Portfolio bundling (min. 500t CO₂e/yr aggregate across participants)", "Program Coordinating Entity (PCE)", "Unified MRV protocol across all participants"],
    timeline:    "18–24 months (programme registration)",
    cta:         "join",
  },
  {
    id:          "article6",
    rank:        4,
    name:        "Article 6 (International Compliance Market)",
    badge:       "Not Eligible (current stage)",
    badgeColor:  "bg-red-50 text-red-600 border-red-200",
    description: "The sovereign compliance market under the Paris Agreement (Article 6.2 bilateral and 6.4 centralized mechanism). Requires host country authorization.",
    why:         "Insufficient scale and requires national authorization. Article 6 is a government-to-government process — DeCarbonIQ does not determine eligibility.",
    requirements:["Host country DNA authorization", "Letter of Authorization (LoA)", "Bilateral agreement between countries", "UNFCCC-accredited MRV protocol"],
    timeline:    "Future pathway via aggregation and national alignment",
    cta:         null,
  },
];

export default function CarbonPathwaysPage() {
  const navigate  = useNavigate();
  const { journeyData } = useJourney();
  const [enrolled, setEnrolled]   = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  const fp  = journeyData?.footprint;
  const opp = journeyData?.opportunity;

  const handleJoin = async () => {
    setEnrolling(true);
    try {
      await client.post("/sme/aggregation-interest", { note: "Via Carbon Pathways page" });
      setEnrolled(true);
      toast.success("Interest registered — our team will contact you within 48 hours.");
    } catch {
      toast.error("Could not register interest. Please try again.");
    }
    setEnrolling(false);
  };

  return (
    <div className="p-6 lg:p-8 min-h-screen">
      {/* Back nav */}
      <button onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-5">
        <ArrowLeft size={13} /> Back
      </button>

      {/* Header */}
      <div className="mb-6">
        
      <PageHeader
        label="Strategic Output"
        title="Carbon Market Pathways"
      />

      <MetricCard label="Available Pathways" value={pathways.length} color="text-slate-900" />
        <p className="text-sm text-slate-500">
          Four structured routes to monetize or credentialize your emissions reductions.
          Eligibility is based on your current emissions data and CRS score.
        </p>
      </div>

      {/* Your profile snapshot */}
      {fp?.total_tco2e && (
        <div className="bg-[#0F172A] rounded-xl p-4 mb-6">
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Your Profile</p>
          <div className="grid grid-cols-3 gap-4">
            {[
              ["Emissions",     `${fp.total_tco2e} tCO₂e/yr`],
              ["Reduction pot.",`${opp?.total_opportunity_tco2e || 0}t/yr`],
              ["Eligibility",   "VCM/Insetting ready"],
            ].map(([l, v]) => (
              <div key={l}>
                <p className="text-xs text-slate-500">{l}</p>
                <p className="text-xs font-bold text-white mt-0.5">{v}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pathway cards */}

      <SectionCard>

      <div className="space-y-4">
        {PATHWAYS.map(p => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden"
            data-testid={`pathway-${p.id}`}>
            <div className="px-5 py-4 flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-slate-400 font-bold">#{p.rank}</span>
                  <p className="text-sm font-semibold text-slate-900">{p.name}</p>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed mb-2">{p.description}</p>
                <div className="bg-slate-50 rounded-lg px-3 py-2 mb-2">
                  <p className="text-xs text-slate-400 font-semibold uppercase mb-0.5">Why this matters for you</p>
                  <p className="text-xs text-slate-600">{p.why}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 font-semibold uppercase mb-1">Requirements</p>
                  <div className="flex flex-wrap gap-1">
                    {p.requirements.map((r, i) => (
                      <span key={i} className="text-xs text-slate-600 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded">{r}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0 text-right">
                <span className={`text-xs font-bold px-2 py-1 rounded-full border block mb-2 ${p.badgeColor}`}>
                  {p.badge}
                </span>
                <p className="text-xs text-slate-400">Timeline</p>
                <p className="text-xs font-semibold text-slate-600">{p.timeline}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CTA — Aggregated Program */}
      <div className="mt-6">
        {!enrolled ? (
          <button
            onClick={() => navigate("/aggregation-enrollment")}
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700 transition-colors"
            data-testid="join-aggregation-btn">
            <GitBranch size={14} /> Join Aggregated Carbon Program
          </button>
        ) : (
          <div className="flex items-center gap-2 px-5 py-3.5 bg-emerald-50 border border-emerald-200 rounded-xl">
            <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-emerald-800">Interest registered</p>
              <p className="text-xs text-emerald-600">Our team will contact you within 48 hours.</p>
            </div>
          </div>
        )}
        <p className="text-xs text-slate-400 text-center mt-2">
          No commitment. DeCarbonIQ does not certify carbon eligibility or represent government positions.
        </p>
      </div>
      </SectionCard>
    </div>
  );
}
